import type { SoundCue } from "../../shared/events";
import { loadAudioSettings, saveAudioSettings } from "./storage";

const CUE_FILES: Record<SoundCue, string> = {
  playerJoined: "/sounds/player_joined.mp3",
  playerLeft: "/sounds/player_left.mp3",
  artistTurnStarts: "/sounds/artist_turn_starts.mp3",
  correctGuess: "/sounds/correct_guess.mp3",
  turnIsOver: "/sounds/turn_is_over.mp3",
  countdownLow: "/sounds/countdown_low.mp3",
  cccSelection: "/sounds/CCC_sfx.mp3",
};

class AudioManager {
  private settings = loadAudioSettings();
  private elements = new Map<SoundCue, HTMLAudioElement>();

  constructor() {
    for (const [cue, src] of Object.entries(CUE_FILES) as [SoundCue, string][]) {
      const audio = new Audio(src);
      audio.preload = "auto";
      audio.volume = this.settings.volume;
      this.elements.set(cue, audio);
    }
  }

  play(cue: SoundCue): void {
    if (this.settings.muted) return;
    const audio = this.elements.get(cue);
    if (!audio) return;
    const instance = audio.cloneNode(true) as HTMLAudioElement;
    instance.volume = this.settings.volume;
    void instance.play().catch(() => {
      // Autoplay can be blocked before the first user gesture; safe to ignore.
    });
  }

  get muted(): boolean {
    return this.settings.muted;
  }

  get volume(): number {
    return this.settings.volume;
  }

  setMuted(muted: boolean): void {
    this.settings.muted = muted;
    saveAudioSettings(this.settings);
  }

  setVolume(volume: number): void {
    this.settings.volume = Math.max(0, Math.min(1, volume));
    for (const audio of this.elements.values()) audio.volume = this.settings.volume;
    saveAudioSettings(this.settings);
  }
}

export const audioManager = new AudioManager();
